/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { ApiClient } from '../types/api/ApiClient';
import { EntityId } from '../types/api/EntityId';
import { IOrganizationUser } from '../types/organization/IOrganizationUser';
import { UserToOrganizationInvitation } from '../types/organization/UserToOrganizationInvitation';
import { UserToOrganizationRegistrationInvitation } from '../types/organization/UserToOrganizationRegistrationInvitation';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';

class OrganizationUserApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  fetchOrganizationUsers = ({ organizationId }: { organizationId: EntityId }) => {
    return this.api.get<IOrganizationUser[]>(`/organization/${organizationId}/user`);
  };

  fetchOrganizationInvitations = ({ organizationId }: { organizationId: EntityId }) => {
    return this.api.get<UserToOrganizationInvitation[]>(`/organization/${organizationId}/user/invitation`);
  };

  removeOrganizationInvitations = ({ organizationId, userId }: { organizationId: EntityId; userId: EntityId }) => {
    return this.api.del(`/organization/${organizationId}/user/${userId}/invitation`);
  };

  fetchOrganizationRegistrationInvitations = ({ organizationId }: { organizationId: EntityId }) => {
    return this.api.get<UserToOrganizationRegistrationInvitation[]>(
      `/organization/${organizationId}/user/registration-invitation`,
    );
  };

  removeOrganizationRegistrationInvitations = ({
    organizationId,
    email,
  }: {
    organizationId: EntityId;
    email: string;
  }) => {
    return this.api.del(`/organization/${organizationId}/user/registration-invitation?email=${btoa(email)}`);
  };

  updateUserRoleInOrganization = (organizationId: EntityId, userId: EntityId, data: { role: string }) => {
    return this.api.patch(`/organization/${organizationId}/user/${userId}`, data);
  };

  deleteUserFromOrganization = ({ organizationId, userId }: { organizationId: EntityId; userId: EntityId }) => {
    return this.api.del(`/organization/${organizationId}/user/${userId}`);
  };
}

export const OrganizationUserApiService = new OrganizationUserApiServiceClass();
